import { BranchContext } from './types';
import { PlanError, request } from './aiPlansApi';

export interface TextIdentity { subject: string; system: string; context: BranchContext; contractId: string; }
export interface TextRequestIds { requestId: string; callId: string; }
export interface RuntimeContextEvent { type: string; at: string; details: Record<string, string | number | boolean | null>; }
export interface RuntimeContextSnapshot { version: 1; capturedAt: string; system: 'iflex'; companyId: string; branchId: string; currentScreen: string | null; events: RuntimeContextEvent[]; }
export interface TextUsage { grantedMicros: number; confirmedMicros: number; reservedMicros: number; pendingMicros: number; availableMicros: number; }

export type ReportFilterScalar = string | number | boolean | null;
export type ReportFilterValue = ReportFilterScalar | ReportFilterScalar[] | { from?: string | null; to?: string | null };
export interface AnalyticsReportGroup { fieldId: number; direction: 'ASC' | 'DESC'; totalize: boolean; }
export interface AnalyticsReportFilter { fieldId: number; value: ReportFilterValue; }
export interface AnalyticsReportPlan {
 datasetId: number; title: string; columns: number[]; groups: AnalyticsReportGroup[];
 filters: AnalyticsReportFilter[]; limit: number;
}
export interface AnalyticsReportPreview {
 mode: 'structure'; filterValuesApplied: false; requestedFilters: AnalyticsReportFilter[];
 data: {
  dataset: { id: string | number; name?: string; area?: string | null };
  columns: Array<{ id: string | number; key: string; label: string; [key: string]: unknown }>;
  groups: Array<{ id: string | number; key: string; label: string; [key: string]: unknown }>;
  filters: Array<Record<string, unknown>>;
  rows: Array<Record<string, ReportFilterScalar>>;
  group_rows?: unknown[];
  limit: number;
 };
}
export type ReportConfirmationState = 'draft' | 'sending' | 'uncertain' | 'saved' | 'failed';
export interface AnalyticsTransientExecutionTotal {
 fieldId: string; key: string; label: string; value: number; dataType: string;
}
export interface AnalyticsTransientExecution {
 mode: 'transient'; filterValuesApplied: true; count: number; limit: number;
 summary: { recordCount: number; totals: AnalyticsTransientExecutionTotal[] } | null;
}
export interface AnalyticsSavedReport {
 id: string; name: string; description: string | null; datasetId: number;
 transientExecution?: AnalyticsTransientExecution;
}
export interface AnalyticsReportConfirmation extends TextRequestIds {
 state: ReportConfirmationState; planHash: string; requiresConfirmation: boolean;
 filterValuesPersisted: false; report: AnalyticsSavedReport | null;
}
export interface AnalyticsReportStatus {
 plan: AnalyticsReportPlan; preview: AnalyticsReportPreview; confirmation: AnalyticsReportConfirmation;
}
export interface TextResult {
 outcome: 'completed' | 'empty' | 'refused' | 'incomplete' | 'failed' | 'uncertain';
 text: string | null;
 receivedText?: string | null;
 source?: string;
 state?: string;
 cost?: { providerMicros?: number; oktonMicros?: number; customerChargeMicros?: number; releasedMicros?: number } | null;
 plan?: AnalyticsReportPlan;
 preview?: AnalyticsReportPreview;
 confirmation?: AnalyticsReportConfirmation;
}
export interface TextExecution {
 call: TextRequestIds & { state: 'reserved' | 'dispatched' | 'uncertain' | 'settled' | 'released'; actual: { brlMicros: number } | null; estimate: { brlMicros: number } };
 result: TextResult | null;
}

const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
const hashPattern = /^[a-f0-9]{64}$/;
const positiveId = (value: any) => Number.isSafeInteger(value) && value > 0;
const amount = (value: any) => Number.isSafeInteger(value) && value >= 0;
const plain = (value: any): value is Record<string, any> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const text = (value: any, max: number) => typeof value === 'string' && value.trim() !== '' && new Blob([value]).size <= max;
const scalar = (value: any): value is ReportFilterScalar => value === null || ['string', 'number', 'boolean'].includes(typeof value);
export const validTextIds = (value: any): value is TextRequestIds => Boolean(value && typeof value.requestId === 'string' && typeof value.callId === 'string' && uuidPattern.test(value.requestId) && uuidPattern.test(value.callId));
const contextBody = (identity: TextIdentity) => ({ companyId: identity.context.companyId, branchId: identity.context.branchId });

function assertScope(value: any, identity: TextIdentity): void {
 if (!value || String(value.companyId) !== identity.context.companyId || String(value.branchId) !== identity.context.branchId || value.contractId !== identity.contractId
  || !Number.isSafeInteger(value.bankId) || value.bankId < 1 || typeof value.tenant !== 'string' || !value.tenant) {
  throw new PlanError(502, 'A resposta não corresponde à filial e ao contrato selecionados.');
 }
}

function validFilterValue(value: any): value is ReportFilterValue {
 if (scalar(value)) return true;
 if (Array.isArray(value)) return value.length <= 100 && value.every(scalar);
 if (!plain(value) || Object.keys(value).some(key => !['from', 'to'].includes(key))) return false;
 return ['from', 'to'].every(key => value[key] === undefined || value[key] === null || typeof value[key] === 'string');
}
function validReportFilter(value: any): value is AnalyticsReportFilter {
 return plain(value) && Object.keys(value).length === 2 && positiveId(value.fieldId) && Object.prototype.hasOwnProperty.call(value, 'value') && validFilterValue(value.value);
}
function validReportPlan(value: any): value is AnalyticsReportPlan {
 if (!plain(value) || Object.keys(value).length !== 6 || !['datasetId', 'title', 'columns', 'groups', 'filters', 'limit'].every(key => Object.prototype.hasOwnProperty.call(value, key))) return false;
 if (!positiveId(value.datasetId) || !text(value.title, 160) || !Number.isSafeInteger(value.limit) || value.limit < 1 || value.limit > 500) return false;
 if (!Array.isArray(value.columns) || value.columns.length < 1 || value.columns.length > 40 || !value.columns.every(positiveId)) return false;
 if (!Array.isArray(value.groups) || value.groups.length > 6 || !value.groups.every((group: any) => plain(group) && Object.keys(group).length === 3
  && positiveId(group.fieldId) && ['ASC', 'DESC'].includes(group.direction) && typeof group.totalize === 'boolean')) return false;
 return Array.isArray(value.filters) && value.filters.length <= 20 && value.filters.every(validReportFilter);
}
function previewField(value: any): boolean {
 return plain(value) && (positiveId(value.id) || (typeof value.id === 'string' && /^[1-9][0-9]*$/.test(value.id)))
  && text(value.key, 120) && text(value.label, 255);
}
function validPreview(value: any, plan: AnalyticsReportPlan): value is AnalyticsReportPreview {
 if (!plain(value) || value.mode !== 'structure' || value.filterValuesApplied !== false
  || !Array.isArray(value.requestedFilters) || value.requestedFilters.length > 20 || !value.requestedFilters.every(validReportFilter)
  || !plain(value.data) || !plain(value.data.dataset) || String(value.data.dataset.id) !== String(plan.datasetId)) return false;
 const data = value.data;
 if (!Array.isArray(data.columns) || data.columns.length > 40 || !data.columns.every(previewField)
  || !Array.isArray(data.groups) || data.groups.length > 6 || !data.groups.every(previewField)
  || !Array.isArray(data.filters) || data.filters.length > 20
  || !Array.isArray(data.rows) || data.rows.length > 20 || !data.rows.every((row: any) => plain(row) && Object.values(row).every(scalar))
  || (data.group_rows !== undefined && (!Array.isArray(data.group_rows) || data.group_rows.length > 500))
  || !Number.isSafeInteger(data.limit) || data.limit < 1 || data.limit > 20) return false;
 const columnIds = new Set(data.columns.map((field: any) => Number(field.id)));
 const groupIds = new Set(data.groups.map((field: any) => Number(field.id)));
 if (!plan.columns.every(id => columnIds.has(id)) || !plan.groups.every(group => groupIds.has(group.fieldId))) return false;
 return plan.filters.length === value.requestedFilters.length
  && plan.filters.every((filter, index) => value.requestedFilters[index]?.fieldId === filter.fieldId);
}
function validTransientExecution(value: any): value is AnalyticsTransientExecution {
 if (!plain(value) || Object.keys(value).length !== 5 || value.mode !== 'transient' || value.filterValuesApplied !== true
  || !Number.isSafeInteger(value.count) || value.count < 0
  || !Number.isSafeInteger(value.limit) || value.limit < 1 || value.limit > 200 || value.count > value.limit) return false;
 if (value.summary === null) return true;
 if (!plain(value.summary) || Object.keys(value.summary).length !== 2
  || !Number.isSafeInteger(value.summary.recordCount) || value.summary.recordCount !== value.count
  || !Array.isArray(value.summary.totals) || value.summary.totals.length > 40) return false;
 return value.summary.totals.every((total: any) => plain(total) && Object.keys(total).length === 5
  && typeof total.fieldId === 'string' && /^[1-9][0-9]*$/.test(total.fieldId)
  && text(total.key, 120) && text(total.label, 255)
  && typeof total.value === 'number' && Number.isFinite(total.value)
  && text(total.dataType, 80));
}
function validSavedReport(value: any, plan: AnalyticsReportPlan): value is AnalyticsSavedReport {
 if (!plain(value)) return false;
 const keys = Object.keys(value);
 if (keys.length < 4 || keys.length > 5 || keys.some(key => !['id', 'name', 'description', 'datasetId', 'transientExecution'].includes(key))) return false;
 if (typeof value.id !== 'string' || !/^[1-9][0-9]*$/.test(value.id)
  || !text(value.name, 255) || (value.description !== null && (typeof value.description !== 'string' || new Blob([value.description]).size > 2000))
  || value.datasetId !== plan.datasetId) return false;
 if (value.transientExecution !== undefined
  && (plan.filters.length === 0 || !validTransientExecution(value.transientExecution))) return false;
 return true;
}
function confirmation(value: any, ids: TextRequestIds, plan: AnalyticsReportPlan): AnalyticsReportConfirmation {
 if (!plain(value) || Object.keys(value).length !== 7 || typeof value.requestId !== 'string' || typeof value.callId !== 'string'
  || !uuidPattern.test(value.requestId) || !uuidPattern.test(value.callId) || value.requestId !== ids.requestId || value.callId !== ids.callId
  || !['draft', 'sending', 'uncertain', 'saved', 'failed'].includes(value.state) || typeof value.planHash !== 'string' || !hashPattern.test(value.planHash)
  || typeof value.requiresConfirmation !== 'boolean' || value.filterValuesPersisted !== false) {
  throw new PlanError(502, 'Não foi possível conferir o estado do relatório.');
 }
 const requires = ['draft', 'sending', 'uncertain'].includes(value.state);
 if (value.requiresConfirmation !== requires || (value.state === 'saved' ? !validSavedReport(value.report, plan) : value.report !== null)) {
  throw new PlanError(502, 'Não foi possível conferir o estado do relatório.');
 }
 return value as AnalyticsReportConfirmation;
}
function reportStatus(value: any, ids: TextRequestIds): AnalyticsReportStatus {
 if (!plain(value) || Object.keys(value).length !== 3 || !validReportPlan(value.plan) || !validPreview(value.preview, value.plan)) {
  throw new PlanError(502, 'Não foi possível conferir o relatório proposto.');
 }
 return { plan: value.plan, preview: value.preview, confirmation: confirmation(value.confirmation, ids, value.plan) };
}
function validCost(value: any): boolean {
 return value === null || value === undefined || (plain(value) && ['providerMicros', 'oktonMicros', 'customerChargeMicros', 'releasedMicros']
  .every(key => value[key] === undefined || amount(value[key])));
}
function validResult(value: any, call: TextExecution['call'], ids: TextRequestIds): value is TextResult {
 if (!plain(value) || !['completed', 'empty', 'refused', 'incomplete', 'failed', 'uncertain'].includes(value.outcome) || !validCost(value.cost)) return false;
 if (value.receivedText !== undefined && value.receivedText !== null
  && (value.outcome !== 'uncertain' || !text(value.receivedText, 1048576))) return false;
 if (value.outcome !== 'completed') return value.text === null && (value.plan === undefined || value.plan === null) && value.preview === undefined && value.confirmation === undefined;
 if (call.state !== 'settled') return false;
 if (typeof value.text === 'string') return value.plan === undefined && value.preview === undefined && value.confirmation === undefined;
 if (value.text !== null || !validReportPlan(value.plan) || !validPreview(value.preview, value.plan)) return false;
 try { confirmation(value.confirmation, ids, value.plan); return true; } catch { return false; }
}
function execution(value: any, identity: TextIdentity, ids: TextRequestIds): TextExecution {
 assertScope(value?.scope, identity);
 const call = value?.call; const result = value?.result;
 if (!call || call.callId !== ids.callId || call.requestId !== ids.requestId || call.subject !== identity.subject || call.systemId !== identity.system
  || !['reserved', 'dispatched', 'uncertain', 'settled', 'released'].includes(call.state) || !amount(call.estimate?.brlMicros)
  || (call.actual !== null && !amount(call.actual?.brlMicros)) || (call.state === 'settled' && call.actual === null)
  || result === undefined || (result !== null && !validResult(result, call, ids))) {
  throw new PlanError(502, 'Não foi possível conferir o resultado. Consulte a mesma solicitação.');
 }
 return { call, result };
}

export async function executeText(token: string, identity: TextIdentity, ids: TextRequestIds, input: string, runtimeContext?: RuntimeContextSnapshot): Promise<TextExecution> {
 if (!validTextIds(ids)) throw new PlanError(422, 'Identificador da solicitação inválido.');
 const body: any = { context: contextBody(identity), ...ids, input };
 if (runtimeContext !== undefined) body.runtimeContext = runtimeContext;
 return execution(await request('/v1/me/ai-plan-executions?system=' + encodeURIComponent(identity.system), token, 'POST', body), identity, ids);
}
export async function recoverText(token: string, identity: TextIdentity, ids: TextRequestIds): Promise<TextExecution> {
 if (!validTextIds(ids)) throw new PlanError(422, 'Identificador da solicitação inválido.');
 return execution(await request('/v1/me/ai-plan-executions/recover?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), callId: ids.callId }), identity, ids);
}
export async function getTextReportStatus(token: string, identity: TextIdentity, ids: TextRequestIds, signal?: AbortSignal): Promise<AnalyticsReportStatus> {
 if (!validTextIds(ids)) throw new PlanError(422, 'Identificador da solicitação inválido.');
 const value = await request('/v1/me/ai-plan-executions/report-status?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), ...ids }, signal);
 return reportStatus(value, ids);
}
export async function confirmTextReport(token: string, identity: TextIdentity, ids: TextRequestIds, planHash: string, plan: AnalyticsReportPlan): Promise<AnalyticsReportConfirmation> {
 if (!validTextIds(ids) || !hashPattern.test(planHash) || !validReportPlan(plan)) throw new PlanError(422, 'Confirmação do relatório inválida.');
 const value = await request('/v1/me/ai-plan-executions/report-confirm?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), ...ids, planHash });
 return confirmation(value, ids, plan);
}
export async function cancelText(token: string, identity: TextIdentity, ids: TextRequestIds): Promise<void> {
 if (!validTextIds(ids)) throw new PlanError(422, 'Identificador da solicitação inválido.');
 const value = await request('/v1/me/ai-plan-executions/cancel?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), ...ids });
 assertScope(value?.scope, identity);
 if (value?.requestId !== ids.requestId || value?.callId !== ids.callId || value?.state !== 'cancelled') {
  throw new PlanError(502, 'Não foi possível confirmar o encerramento. Consulte a mesma solicitação.');
 }
}
export async function getTextAvailability(token: string, identity: TextIdentity, signal?: AbortSignal): Promise<boolean> {
 const value = await request('/v1/me/ai-plan-executions/availability?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity) }, signal);
 assertScope(value?.scope, identity);
 if (typeof value?.generationEnabled !== 'boolean') throw new PlanError(502, 'Não foi possível conferir a disponibilidade da geração.');
 return value.generationEnabled;
}
export async function getTextUsage(token: string, identity: TextIdentity, signal?: AbortSignal): Promise<TextUsage> {
 const value = await request('/v1/me/ai-plan-usage?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity) }, signal);
 assertScope(value?.scope, identity); const usage = value?.usage;
 if (!usage || !['grantedMicros', 'confirmedMicros', 'reservedMicros', 'pendingMicros', 'availableMicros'].every(field => amount(usage[field]))
  || usage.availableMicros !== Math.max(0, usage.grantedMicros - usage.confirmedMicros - usage.reservedMicros - usage.pendingMicros)) throw new PlanError(502, 'Saldo inválido. Atualize antes de enviar.');
 return usage;
}
export function newTextRequest(): TextRequestIds {
 const uuid = () => {
  const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
 };
 return { requestId: uuid(), callId: uuid() };
}
export const textStorageKey = (identity: TextIdentity) => 'okton.ai.text.v1:' + JSON.stringify([identity.subject, identity.system, identity.context.companyId, identity.context.branchId, identity.contractId]);
export function loadTextRequest(key: string): TextRequestIds | null {
 const raw = window.sessionStorage.getItem(key); if (raw === null) return null;
 const value = JSON.parse(raw);
 if (!validTextIds(value) || Object.keys(value).length !== 2) throw new Error('Não foi possível conferir a solicitação salva neste navegador.');
 return { requestId: value.requestId, callId: value.callId };
}
export function saveTextRequest(key: string, ids: TextRequestIds): void {
 if (!validTextIds(ids)) throw new Error('Identificador da solicitação inválido.');
 try {
  window.sessionStorage.setItem(key, JSON.stringify(ids));
  if (window.sessionStorage.getItem(key) !== JSON.stringify(ids)) throw new Error();
 } catch { throw new Error('Não foi possível guardar a solicitação neste navegador. Nenhuma geração foi enviada.'); }
}
export const textBytes = (value: string) => new Blob([value]).size;
export const textMoney = (micros: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(micros / 1000000);

export interface TextHistoryItem extends TextRequestIds {
 systemId: string; subject: string;
 scope: { bankId: number; tenant: string; companyId: number; branchId: number; contractId: string };
 state: TextExecution['call']['state']; reservedMicros: number; actualMicros: number | null; createdAt: string; updatedAt: string;
}
export interface TextHistoryPage { items: TextHistoryItem[]; nextOffset: number | null; }
export const textStatuses: Record<TextExecution['call']['state'], string> = {
 reserved: 'Aguardando confirmação do envio', dispatched: 'Aguardando resposta', uncertain: 'Aguardando conferência da Okton',
 settled: 'Solicitação concluída', released: 'Solicitação encerrada',
};
export function validHistoryItem(item: any): item is TextHistoryItem {
 return Boolean(item) && validTextIds({ requestId: item.requestId, callId: item.callId }) && typeof item.systemId === 'string' && typeof item.subject === 'string'
  && !!item.scope && Number.isSafeInteger(item.scope.bankId) && item.scope.bankId > 0
  && Number.isSafeInteger(item.scope.companyId) && item.scope.companyId > 0
  && Number.isSafeInteger(item.scope.branchId) && item.scope.branchId > 0
  && typeof item.scope.tenant === 'string' && /^[a-f0-9]{32}$/.test(item.scope.contractId)
  && Object.prototype.hasOwnProperty.call(textStatuses, item.state) && amount(item.reservedMicros)
  && (item.actualMicros === null || amount(item.actualMicros))
  && typeof item.createdAt === 'string' && Number.isFinite(Date.parse(item.createdAt))
  && typeof item.updatedAt === 'string' && Number.isFinite(Date.parse(item.updatedAt));
}
export function validHistoryPage(value: any): value is TextHistoryPage {
 return !!value && Array.isArray(value.items) && value.items.length <= 50 && value.items.every(validHistoryItem)
  && (value.nextOffset === null || (Number.isSafeInteger(value.nextOffset) && value.nextOffset > 0 && value.nextOffset <= 100050));
}
export async function getTextHistory(token: string, identity: TextIdentity, offset = 0, signal?: AbortSignal): Promise<TextHistoryPage> {
 const value = await request('/v1/me/ai-plan-executions/history?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), offset }, signal);
 assertScope(value?.scope, identity);
 if (!validHistoryPage(value)) throw new PlanError(502, 'Histórico inválido. Atualize para consultar.');
 value.items.forEach(item => {
  assertScope(item.scope, identity);
  if (item.subject !== identity.subject || item.systemId !== identity.system) throw new PlanError(502, 'O histórico não corresponde ao usuário e sistema selecionados.');
 });
 return value;
}
